import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAppLocale } from '@/hooks/useAppLocale'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { cn } from '@/lib/utils'
import { enGB, th } from 'date-fns/locale'
import { CalendarIcon } from 'lucide-react'
import { useState } from 'react'

type DatePickerProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  startYear?: number
  endYear?: number
  testId?: string
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'dd/mm/yyyy',
  disabled,
  className,
  startYear = 1900,
  endYear = new Date().getFullYear() + 20,
  testId,
}: DatePickerProps) {
  const locale = useAppLocale()
  const { formatDateTime } = useDateFormatter()
  const [open, setOpen] = useState(false)

  const rawDate = value ? new Date(value) : undefined
  const selectedDate = rawDate && !isNaN(rawDate.getTime()) ? rawDate : undefined

  function handleSelect(date?: Date) {
    if (!date) return

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    onChange(`${year}-${month}-${day}`)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testId}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'h-9 w-full justify-start bg-secondary/50 px-3 text-left text-xs font-normal',
            !selectedDate && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 size-3.5" />
          {selectedDate
            ? formatDateTime(selectedDate, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          captionLayout="dropdown"
          selected={selectedDate}
          defaultMonth={selectedDate}
          startMonth={new Date(startYear, 0)}
          endMonth={new Date(endYear, 11)}
          onSelect={handleSelect}
          locale={locale === 'th-TH' ? th : enGB}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}
